import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import { QrPosterTemplate } from '../models/qr-poster-template.model';
import { PosterTextSet } from '../models/qr-poster-texts.model';
import { QrPosterRenderDescriptorResponse } from '../models/qr-poster-render-descriptor.model';
import { QrPosterTemplateRendererService } from './qr-poster-template-renderer.service';
import {
  QrPosterRenderAssetLoader,
  QrPosterRenderAssetsService,
} from './qr-poster-render-assets.service';
import { QrPosterRenderContextBuilderService } from './qr-poster-render-context-builder.service';

export interface QrPosterDocumentOptions {
  templateOverride?: QrPosterTemplate;
  posterTextsOverride?: PosterTextSet;
  editorDraftFooterEnabled?: boolean;
  renderSource?: string;
  prestartedLogoBlob?: Promise<Blob | null>;
  assetLoader?: QrPosterRenderAssetLoader;
}

@Injectable({ providedIn: 'root' })
export class QrPosterDocumentService {
  constructor(
    private readonly assetsService: QrPosterRenderAssetsService,
    private readonly contextBuilder: QrPosterRenderContextBuilderService,
    private readonly templateRenderer: QrPosterTemplateRendererService,
  ) {}

  async createPdfBlob(
    response: QrPosterRenderDescriptorResponse,
    options: QrPosterDocumentOptions = {},
  ): Promise<Blob> {
    const pdf = this.createA5Pdf();
    await this.renderDescriptorToPage(pdf, response, options);
    return pdf.output('blob');
  }

  async createCombinedPdfBlob(
    responses: QrPosterRenderDescriptorResponse[],
    options: QrPosterDocumentOptions = {},
  ): Promise<Blob> {
    if (!responses.length) {
      throw new Error('At least one QR poster render descriptor is required');
    }

    const pdf = this.createA5Pdf();
    for (let index = 0; index < responses.length; index += 1) {
      if (index > 0) {
        pdf.addPage('a5', 'portrait');
      }
      await this.renderDescriptorToPage(pdf, responses[index], options);
    }
    return pdf.output('blob');
  }

  async renderDescriptorToPage(
    pdf: jsPDF,
    response: QrPosterRenderDescriptorResponse,
    options: QrPosterDocumentOptions = {},
  ): Promise<void> {
    const renderResponse = this.withOverrides(response, options);
    const assetsPromise = this.assetsService.prepare(
      renderResponse,
      options.assetLoader,
      { prestartedLogoBlob: options.prestartedLogoBlob },
    );
    const qrDataUrlPromise = QRCode.toDataURL(renderResponse.descriptor.qrDestination, {
      errorCorrectionLevel: 'H',
      margin: 0,
    });
    let assets: Awaited<ReturnType<QrPosterRenderAssetsService['prepare']>> | null = null;
    try {
      const [preparedAssets, qrDataUrl] = await Promise.all([assetsPromise, qrDataUrlPromise]);
      assets = preparedAssets;
      const context = this.contextBuilder.build(renderResponse.descriptor, assets, qrDataUrl);
      const footerEnabled = options.editorDraftFooterEnabled
        ?? renderResponse.descriptor.branding.footerEnabled;

      await this.templateRenderer.renderTemplate({
        pdf,
        template: assets.template,
        context,
        language: renderResponse.descriptor.renderLanguage,
        enforceScanafyFooter: footerEnabled,
        footerEnabled,
        renderSource: options.renderSource || 'canonical-qr-poster-document',
      });
    } finally {
      if (assets) {
        assets.dispose();
      } else {
        void assetsPromise.then((preparedAssets) => preparedAssets.dispose()).catch(() => undefined);
      }
    }
  }

  async download(
    response: QrPosterRenderDescriptorResponse,
    fileName: string,
    options: QrPosterDocumentOptions = {},
  ): Promise<void> {
    const blob = await this.createPdfBlob(response, options);
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  private createA5Pdf(): jsPDF {
    return new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a5' });
  }

  private withOverrides(
    response: QrPosterRenderDescriptorResponse,
    options: QrPosterDocumentOptions,
  ): QrPosterRenderDescriptorResponse {
    if (!options.templateOverride && !options.posterTextsOverride) {
      return response;
    }

    const template = options.templateOverride || response.descriptor.template;

    return {
      ...response,
      descriptor: {
        ...response.descriptor,
        template,
        posterTexts: options.posterTextsOverride || response.descriptor.posterTexts,
        templateImageAssetKeys: [...new Set(template.blocks
          .filter((block) => block.type === 'image')
          .map((block) => block.content?.imageAssetKey || '')
          .filter(Boolean))],
      },
    };
  }
}
